import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";

export type CopyReviewActionRow = {
  id?: string;
  kind?: string;
  slide?: number | null;
  label?: string;
  detail?: string;
  before?: { text?: string; sub?: string };
  after?: { text?: string; sub?: string };
  patch?: { index?: number; text?: string; sub?: string; why?: string; numbered?: boolean };
};

function fieldDiffVisible(before?: string, after?: string): boolean {
  if (before == null && after == null) return false;
  if (before != null && after != null && before === after) return false;
  return true;
}

function FieldDiff({
  label,
  before,
  after,
}: {
  label: string;
  before?: string;
  after?: string;
}) {
  if (!fieldDiffVisible(before, after)) return null;
  return (
    <div className="mt-1 space-y-0.5">
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
      {before != null ? (
        <p className="whitespace-pre-wrap text-red-800/90 line-through decoration-red-700/50">
          {before || "∅"}
        </p>
      ) : null}
      {after != null ? (
        <p className="whitespace-pre-wrap text-emerald-900">{after || "∅"}</p>
      ) : null}
    </div>
  );
}

export function CopyReviewChanges({
  actions,
  reviewVerdict,
  itemId,
  disabled,
  pending,
  error,
  onApply,
  onReject,
  compact,
}: {
  actions: CopyReviewActionRow[];
  reviewVerdict?: string;
  itemId?: string;
  disabled?: boolean;
  pending?: boolean;
  error?: string;
  onApply: (ids: string[], rejectIds: string[]) => void;
  onReject?: (id: string) => void;
  compact?: boolean;
}) {
  const rows = (actions || []).filter((a) => a?.id);
  const key = `${itemId || ""}:${rows.map((a) => a.id).join("|")}`;
  const [picked, setPicked] = useState<Record<string, boolean>>({});
  useEffect(() => {
    const next: Record<string, boolean> = {};
    for (const row of rows) {
      if (row.id) next[row.id] = true;
    }
    setPicked(next);
  }, [key]);

  if (!rows.length) {
    return (
      <p className="text-xs text-muted-foreground">
        {reviewVerdict === "rewrite"
          ? "Recenzja każe poprawić, ale bez konkretnych patchy. Kliknij „Popraw po recenzji” albo wygeneruj treść ponownie."
          : "Brak konkretnych zastrzeżeń do odhaczenia (em dash, VERSALIKI albo patche)."}
      </p>
    );
  }

  const selected = rows.filter((r) => r.id && picked[r.id]).map((r) => String(r.id));
  const rejected = rows.filter((r) => r.id && !picked[r.id]).map((r) => String(r.id));

  return (
    <div className={compact ? "space-y-2" : "space-y-2 rounded-md border border-slate-200 bg-slate-50/80 p-3"}>
      <p className="text-xs font-medium text-slate-800">Zmiany do zatwierdzenia</p>
      <p className="text-[11px] text-muted-foreground">
        Odznaczone przy „Wdróż zaznaczone” zostaną odrzucone. Pojedyncze Zatwierdź zostawia resztę na później.
      </p>
      <ul className="space-y-3">
        {rows.map((row) => {
          const id = String(row.id || "");
          const showHaslo = fieldDiffVisible(row.before?.text, row.after?.text);
          const showOpis = fieldDiffVisible(row.before?.sub, row.after?.sub);
          const hasFieldDiff = showHaslo || showOpis;
          const numberedPatch =
            row.patch && Object.prototype.hasOwnProperty.call(row.patch, "numbered")
              ? Boolean(row.patch.numbered)
              : null;
          const detail =
            (row.detail || "").trim() ||
            (numberedPatch === true
              ? "Włącz numer przykładu"
              : numberedPatch === false
                ? "Wyłącz numer przykładu"
                : "");
          const showFallback = !hasFieldDiff && !detail;
          return (
            <li key={id} className="rounded border border-slate-200 bg-white p-2 text-xs">
              <div className="flex items-start gap-2">
                <input
                  id={`copy-review-${id}`}
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 accent-brand"
                  checked={Boolean(picked[id])}
                  disabled={disabled || pending}
                  onChange={(e) => setPicked((prev) => ({ ...prev, [id]: e.target.checked }))}
                />
                <label htmlFor={`copy-review-${id}`} className="min-w-0 flex-1 cursor-pointer">
                  <span className="font-medium text-slate-900">{row.label || id}</span>
                  {detail ? (
                    <span className="mt-0.5 block text-muted-foreground">{detail}</span>
                  ) : null}
                </label>
              </div>
              {hasFieldDiff || showFallback ? (
                <div className="mt-2 border-t border-slate-100 pt-2 pl-6">
                  {showHaslo ? (
                    <FieldDiff label="Hasło" before={row.before?.text} after={row.after?.text} />
                  ) : null}
                  {showOpis ? (
                    <FieldDiff label="Opis" before={row.before?.sub} after={row.after?.sub} />
                  ) : null}
                  {showFallback ? (
                    <p className="text-muted-foreground">Zmiana bez podglądu tekstu (np. flaga składu).</p>
                  ) : null}
                </div>
              ) : null}
              <div className="mt-2 flex flex-wrap gap-2 pl-6">
                <Button
                  type="button"
                  size="sm"
                  variant="brand"
                  disabled={disabled || pending}
                  onClick={() => onApply([id], [])}
                >
                  Zatwierdź
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={disabled || pending}
                  onClick={() => (onReject ? onReject(id) : onApply([], [id]))}
                >
                  Odrzuć
                </Button>
              </div>
            </li>
          );
        })}
      </ul>
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          size="sm"
          variant="brand"
          disabled={disabled || pending || !selected.length}
          onClick={() => onApply(selected, rejected)}
        >
          {pending ? "Wdrażam…" : "Wdróż zaznaczone"}
        </Button>
        <button
          type="button"
          className="text-xs text-muted-foreground underline-offset-2 hover:underline"
          disabled={disabled || pending}
          onClick={() => {
            const next: Record<string, boolean> = {};
            for (const row of rows) {
              if (row.id) next[row.id] = true;
            }
            setPicked(next);
          }}
        >
          Zaznacz wszystkie
        </button>
        <button
          type="button"
          className="text-xs text-muted-foreground underline-offset-2 hover:underline"
          disabled={disabled || pending}
          onClick={() => setPicked({})}
        >
          Odznacz
        </button>
      </div>
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
}

export function CopySlidesDraft({
  slides,
  label,
}: {
  slides: { type?: string; text?: string; heading?: string; sub?: string; body?: string }[];
  label?: string;
}) {
  if (!slides.length) return null;
  return (
    <div className="space-y-2">
      {label ? <p className="text-xs font-medium text-slate-800">{label}</p> : null}
      {slides.map((slide, i) => {
        const text = String(slide.text || slide.heading || "");
        const sub = String(slide.sub || slide.body || "");
        return (
          <div key={`draft-${i}`} className="rounded border border-border/60 bg-muted/30 p-2 text-xs">
            <p className="mb-1 font-medium text-muted-foreground">
              Slajd {i + 1}
              {slide.type ? ` · ${slide.type}` : ""}
            </p>
            {text ? <p className="whitespace-pre-wrap font-medium">{text}</p> : null}
            {sub ? <p className="mt-1 whitespace-pre-wrap text-muted-foreground">{sub}</p> : null}
          </div>
        );
      })}
    </div>
  );
}
